import {Menu, MenuButton, MenuItems, MenuItem} from '@headlessui/react';
import {ChevronDownIcon} from '@heroicons/react/20/solid';
import {platform} from '@todesktop/client-core';
import {useState} from 'react';
import {DesktopSourcePickerModal} from './desktop-source-picker-modal';
import {MENU_CONFIG, MENU_ITEM_CONFIG, BUTTON_STYLE_MAIN_FLEX, BUTTON_STYLE_PUB} from './styles';
import * as U from './util';

type PublishDropdownProps = {
  isPublishingCamera: boolean;
  isPublishingScreenshare: boolean;
  handlePublishCamera: (payload: PublishCameraPayload) => void;
  handlePublishScreenshare: (payload: PublishScreensharePayload) => void;
};

export type PublishCameraPayload = {initialAudioMute: boolean; initialVideoMute: boolean; publishAudioOnly: boolean};
export type PublishScreensharePayload = {publishVideoOnly: boolean; screenShareSourceId: string | undefined};

export function PublishDropdown({
  isPublishingCamera,
  isPublishingScreenshare,
  handlePublishCamera,
  handlePublishScreenshare,
}: PublishDropdownProps) {
  const [pendingSourcePickerPayload, setPendingSourcePickerPayload] = useState<null | PublishScreensharePayload>(null);
  const [isDesktopSourcePickerOpen, setIsDesktopSourcePickerOpen] = useState(false);

  type PublishDropdownItem = {
    label: string;
    payload: PublishCameraPayload;
  };

  type ScreenShareDropdownItem = {
    label: string;
    payload: PublishScreensharePayload;
  };

  const PUBLISH_BUTTONS: PublishDropdownItem[] = [
    {
      label: 'Publish Audio',
      payload: {
        initialAudioMute: false,
        initialVideoMute: false,
        publishAudioOnly: true,
      },
    },
    {
      label: 'Publish Audio + Video',
      payload: {
        initialAudioMute: false,
        initialVideoMute: false,
        publishAudioOnly: false,
      },
    },
    {
      label: 'Publish Audio muted + Video',
      payload: {
        initialAudioMute: true,
        initialVideoMute: false,
        publishAudioOnly: false,
      },
    },
    {
      label: 'Publish Audio + Video muted',
      payload: {
        initialAudioMute: false,
        initialVideoMute: true,
        publishAudioOnly: false,
      },
    },
    {
      label: 'Publish Audio muted + Video muted',
      payload: {
        initialAudioMute: true,
        initialVideoMute: true,
        publishAudioOnly: false,
      },
    },
  ];

  const SCREEN_SHARE_BUTTONS: ScreenShareDropdownItem[] = [
    {label: 'Screenshare Video Only', payload: {publishVideoOnly: true, screenShareSourceId: undefined}},
    {label: 'Screenshare Audio + Video', payload: {publishVideoOnly: false, screenShareSourceId: undefined}},
  ];

  function handleClickScreenshare(payload: PublishScreensharePayload) {
    if (platform.todesktop.isDesktopApp()) {
      setPendingSourcePickerPayload(payload);
      setIsDesktopSourcePickerOpen(true);
    } else {
      handlePublishScreenshare(payload);
    }
  }

  function handleSetScreenShareSource(id: string) {
    if (!pendingSourcePickerPayload) {
      U.appLog('No pending payload found');
      setIsDesktopSourcePickerOpen(false);
      return;
    }
    pendingSourcePickerPayload.screenShareSourceId = id;
    handlePublishScreenshare(pendingSourcePickerPayload);
    setIsDesktopSourcePickerOpen(false);
  }

  return (
    <>
      <Menu as="div" className={MENU_CONFIG}>
        {() => (
          <div>
            <MenuButton className={BUTTON_STYLE_MAIN_FLEX}>
              Publish ...
              <ChevronDownIcon className="w-4 ml-1" />
            </MenuButton>
            <MenuItems className={MENU_ITEM_CONFIG}>
              <div className="py-1">
                {PUBLISH_BUTTONS.map(item => (
                  <MenuItem key={item.label}>
                    <button
                      className={BUTTON_STYLE_PUB}
                      onClick={() => handlePublishCamera(item.payload)}
                      disabled={isPublishingCamera}
                    >
                      {item.label}
                    </button>
                  </MenuItem>
                ))}
              </div>
              <div className="py-1">
                {SCREEN_SHARE_BUTTONS.map(item => (
                  <MenuItem key={item.label}>
                    <button
                      className={BUTTON_STYLE_PUB}
                      onClick={() => handleClickScreenshare(item.payload)}
                      disabled={isPublishingScreenshare}
                    >
                      {item.label}
                    </button>
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </div>
        )}
      </Menu>
      <DesktopSourcePickerModal
        isOpen={isDesktopSourcePickerOpen}
        setOpen={setIsDesktopSourcePickerOpen}
        setScreenShareSourceId={id => handleSetScreenShareSource(id)}
      />
    </>
  );
}
